import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone, Mail } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CONTACT, MUNICIPIOS, CADEIAS, MAPS_LINK, srcSetFor } from "@/lib/content";

export const Region = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const clip = useTransform(scrollYProgress, [0, 0.4], ["inset(12% 8% 12% 8% round 32px)", "inset(0% 0% 0% 0% round 0px)"]);

  return (
    <section id="atuacao" ref={ref} data-testid="region-section" className="relative">
      <motion.div style={{ clipPath: clip }} className="relative h-[56svh] min-h-[440px] overflow-hidden md:h-[90svh] md:min-h-[560px]">
        <motion.img style={{ y, scale: 1.25 }} src="/img/pastagem-gado.webp" alt="Gado de corte em pastagem da Metade Sul do Rio Grande do Sul" srcSet={srcSetFor("/img/pastagem-gado.webp")} sizes="100vw" width={961} height={1280} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1B2F1D]/55" />
        <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-16 text-[#F9F8F3] sm:px-8 md:pb-24">
          <Reveal><Overline light>Onde atuamos</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.98] tracking-tight sm:text-6xl lg:text-8xl">
              Sediados em Pelotas. <em className="text-[#8FC690]">Presentes em toda a metade sul do RS.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/15 backdrop-blur-md sm:grid-cols-3">
              {[
                { icon: MapPin, label: "Endereço", value: `${CONTACT.address} · ${CONTACT.city}`, href: MAPS_LINK, testid: "region-info-endereco" },
                { icon: Phone, label: "Telefone / WhatsApp", value: CONTACT.phone, href: `tel:+${CONTACT.wa}`, testid: "region-info-telefone" },
                { icon: Mail, label: "E-mail", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "region-info-email" },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noreferrer" : undefined}
                  data-testid={c.testid}
                  className="bg-[#1B2F1D]/50 p-6 transition-colors duration-300 hover:bg-[#1B2F1D]/70"
                >
                  <c.icon className="h-5 w-5 text-[#8FC690]" />
                  <p className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#F9F8F3]/70">{c.label}</p>
                  <p className="mt-1 break-words text-base font-medium">{c.value}</p>
                </a>
              ))}
            </div>
            <div data-testid="region-municipios" className="mt-4 rounded-2xl border border-white/15 bg-[#1B2F1D]/60 p-6 backdrop-blur-md">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#F9F8F3]/70">22 municípios atendidos na Metade Sul do RS</p>
              <p className="mt-3 text-sm leading-relaxed text-[#F9F8F3]/85">
                {MUNICIPIOS.map((m, i) => (
                  <span key={m}>
                    {m === "Pelotas" ? (
                      <Link data-testid="region-pelotas-link" to="/municipios/pelotas" className="font-semibold text-[#8FC690] underline underline-offset-2">Pelotas</Link>
                    ) : (
                      m
                    )}
                    {i < MUNICIPIOS.length - 1 ? " · " : "."}
                  </span>
                ))}
              </p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-[#F9F8F3]/70">Cadeias produtivas</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CADEIAS.map((c) => (
                  <span key={c} className="rounded-full border border-[#8FC690]/40 px-3.5 py-1.5 text-sm text-[#F9F8F3]/90">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </motion.div>
    </section>
  );
};
